import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import {
  Upload,
  Camera,
  FileText,
  Send,
  Loader2,
  Calendar,
  Euro,
  User,
  Trash2,
  Mail,
  CheckCircle,
  Clock,
  FolderOpen,
  Receipt,
  Image,
  Filter,
} from "lucide-react";

const API = window.location.origin + "/api";

const DocumentiContabiliPage = () => {
  const [documenti, setDocumenti] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Filters
  const [filterCollaboratore, setFilterCollaboratore] = useState("");
  const [filterMese, setFilterMese] = useState("");
  
  // Upload form
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    collaboratore: "",
    data_documento: new Date().toISOString().split("T")[0],
    importo: "",
    descrizione: "",
    tipo_documento: "scontrino"
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Fetch documents
  const fetchDocumenti = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filterCollaboratore) params.append("collaboratore", filterCollaboratore);
      if (filterMese) params.append("mese", filterMese);
      
      const res = await axios.get(`${API}/admin/documenti-contabili?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumenti(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento documenti");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/documenti-contabili/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDocumenti();
    fetchStats();
  }, [filterCollaboratore, filterMese]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowUploadDialog(true);
    }
  };

  // Upload document
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Seleziona un file");
      return;
    }
    if (!uploadForm.collaboratore) {
      toast.error("Seleziona il collaboratore");
      return;
    }
    if (!uploadForm.data_documento) {
      toast.error("Inserisci la data del documento");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("collaboratore", uploadForm.collaboratore);
      formData.append("data_documento", uploadForm.data_documento);
      if (uploadForm.importo) formData.append("importo", uploadForm.importo);
      if (uploadForm.descrizione) formData.append("descrizione", uploadForm.descrizione);
      formData.append("tipo_documento", uploadForm.tipo_documento);

      await axios.post(`${API}/admin/documenti-contabili/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Documento caricato!");
      setShowUploadDialog(false);
      setSelectedFile(null);
      setUploadForm({
        collaboratore: uploadForm.collaboratore, // Keep collaboratore for next upload
        data_documento: new Date().toISOString().split("T")[0],
        importo: "",
        descrizione: "",
        tipo_documento: "scontrino"
      });
      fetchDocumenti();
      fetchStats();
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  // Delete document
  const handleDelete = async (documento_id) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/documenti-contabili/${documento_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Documento eliminato");
      fetchDocumenti();
      fetchStats();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Send to accountant
  const handleSendToAccountant = async () => {
    if (!window.confirm("Inviare tutti i documenti non ancora inviati alla commercialista?")) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filterMese) params.append("mese", filterMese);
      if (filterCollaboratore) params.append("collaboratore", filterCollaboratore);
      
      const res = await axios.post(`${API}/admin/documenti-contabili/invia-commercialista?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Email inviata a ${res.data.destinatario} con ${res.data.documenti_inviati} documenti`);
      fetchDocumenti();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  // Get current month options
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  };

  // Group documents by collaboratore
  const documentsByCollaboratore = documenti.reduce((acc, doc) => {
    const collab = doc.collaboratore;
    if (!acc[collab]) acc[collab] = [];
    acc[collab].push(doc);
    return acc;
  }, {});

  const collaboratori = stats.collaboratori || ["Aldo", "Davide", "Cinzia", "Emanuela"];

  return (
    <PageLayout title="Documenti Contabili" subtitle="Gestione scontrini e fatture">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Receipt className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">{documenti.length}</p>
                <p className="text-xs text-purple-600">Documenti totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.da_inviare || 0}</p>
                <p className="text-xs text-amber-600">Da inviare</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {documenti.filter(d => d.inviato_commercialista).length}
                </p>
                <p className="text-xs text-green-600">Già inviati</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Euro className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  € {documenti.reduce((sum, d) => sum + (d.importo || 0), 0).toFixed(2)}
                </p>
                <p className="text-xs text-blue-600">Totale importi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Upload buttons */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,application/pdf"
              className="hidden"
            />
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              capture="environment"
              className="hidden"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Upload className="w-4 h-4 mr-2" />
              Carica File
            </Button>
            
            <Button
              onClick={() => cameraInputRef.current?.click()}
              variant="outline"
              className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              <Camera className="w-4 h-4 mr-2" />
              Scatta Foto
            </Button>

            <div className="h-6 w-px bg-gray-300" />

            {/* Filters */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <Select value={filterCollaboratore || "all"} onValueChange={(v) => setFilterCollaboratore(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px] rounded-full">
                  <SelectValue placeholder="Collaboratore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {collaboratori.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterMese || "all"} onValueChange={(v) => setFilterMese(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px] rounded-full">
                  <SelectValue placeholder="Mese" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i mesi</SelectItem>
                  {getMonthOptions().map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1" />

            {/* Send to accountant */}
            <Button
              onClick={handleSendToAccountant}
              disabled={sending || stats.da_inviare === 0}
              className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Invia a Commercialista
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents by Collaboratore */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : documenti.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl text-gray-500">Nessun documento caricato</p>
            <p className="text-sm text-gray-400 mt-2">
              Usa i pulsanti sopra per caricare scontrini o fatture
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(documentsByCollaboratore).map(([collab, docs]) => (
            <Card key={collab}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-600" />
                    {collab}
                    <Badge variant="secondary">{docs.length} documenti</Badge>
                  </div>
                  <span className="text-sm font-normal text-gray-500">
                    Totale: € {docs.reduce((sum, d) => sum + (d.importo || 0), 0).toFixed(2)}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left text-xs font-medium text-gray-500">Data</th>
                        <th className="p-3 text-left text-xs font-medium text-gray-500">Tipo</th>
                        <th className="p-3 text-left text-xs font-medium text-gray-500">Descrizione</th>
                        <th className="p-3 text-left text-xs font-medium text-gray-500">Importo</th>
                        <th className="p-3 text-left text-xs font-medium text-gray-500">File</th>
                        <th className="p-3 text-left text-xs font-medium text-gray-500">Stato</th>
                        <th className="p-3 text-left text-xs font-medium text-gray-500">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map(doc => (
                        <tr key={doc.documento_id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm">{doc.data_documento}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="capitalize">
                              {doc.tipo_documento}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-gray-600 max-w-[200px] truncate">
                            {doc.descrizione || "-"}
                          </td>
                          <td className="p-3 text-sm font-medium">
                            {doc.importo ? `€ ${doc.importo.toFixed(2)}` : "-"}
                          </td>
                          <td className="p-3">
                            <a
                              href={`${window.location.origin}${doc.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-purple-600 hover:text-purple-800 text-sm"
                            >
                              {doc.file_type === "pdf" ? (
                                <FileText className="w-4 h-4" />
                              ) : (
                                <Image className="w-4 h-4" />
                              )}
                              Apri
                            </a>
                          </td>
                          <td className="p-3">
                            {doc.inviato_commercialista ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Inviato
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Clock className="w-3 h-3 mr-1" />
                                Da inviare
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(doc.documento_id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              Carica Documento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File preview */}
            {selectedFile && (
              <div className="p-3 bg-purple-50 rounded-xl flex items-center gap-3">
                {selectedFile.type.startsWith("image") ? (
                  <Image className="w-8 h-8 text-purple-600" />
                ) : (
                  <FileText className="w-8 h-8 text-purple-600" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            )}

            {/* Collaboratore */}
            <div className="space-y-2">
              <Label>Collaboratore *</Label>
              <Select
                value={uploadForm.collaboratore}
                onValueChange={(v) => setUploadForm({...uploadForm, collaboratore: v})}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona collaboratore" />
                </SelectTrigger>
                <SelectContent>
                  {collaboratori.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Data documento */}
            <div className="space-y-2">
              <Label>Data documento *</Label>
              <Input
                type="date"
                value={uploadForm.data_documento}
                onChange={(e) => setUploadForm({...uploadForm, data_documento: e.target.value})}
                className="rounded-xl"
              />
            </div>

            {/* Tipo documento */}
            <div className="space-y-2">
              <Label>Tipo documento</Label>
              <Select
                value={uploadForm.tipo_documento}
                onValueChange={(v) => setUploadForm({...uploadForm, tipo_documento: v})}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scontrino">Scontrino</SelectItem>
                  <SelectItem value="fattura">Fattura</SelectItem>
                  <SelectItem value="ricevuta">Ricevuta</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Importo */}
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={uploadForm.importo}
                onChange={(e) => setUploadForm({...uploadForm, importo: e.target.value})}
                placeholder="0.00"
                className="rounded-xl"
              />
            </div>

            {/* Descrizione */}
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={uploadForm.descrizione}
                onChange={(e) => setUploadForm({...uploadForm, descrizione: e.target.value})}
                placeholder="Es: Pranzo cliente, Carburante..."
                className="rounded-xl"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadDialog(false);
                setSelectedFile(null);
              }}
              className="rounded-full"
            >
              Annulla
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default DocumentiContabiliPage;
